import React from 'react';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UploadedEmployee, ApprovedBranch } from '../../../hooks/useEmployeeHandlers';
import { EMPLOYMENT_TYPE_OPTIONS, RESIDENCY_STATUS_OPTIONS } from '../../../hooks/useEmployeeHandlers';
import type { UploadedEmployeeErrors } from '../dialogs/UploadPreviewDialog';

interface UploadPreviewMobileProps {
    employees: UploadedEmployee[];
    branches?: ApprovedBranch[];
    branchesLoading?: boolean;
    onChange: (rowId: number, field: keyof UploadedEmployee, value: any) => void;
    errors?: UploadedEmployeeErrors;
    showAllErrors?: boolean;
}

const TEXT_FIELDS: [string, keyof UploadedEmployee][] = [
    ['employee.form.field.firstName',  'firstName'],
    ['employee.form.field.lastName',   'lastName'],
    ['employee.form.field.personalNo', 'personalNo'],
    ['employee.form.field.email',      'email'],
    ['employee.form.field.phone',      'phone'],
    ['employee.form.field.taxId',      'taxId'],
];

const UploadPreviewMobile: React.FC<UploadPreviewMobileProps> = ({
    employees,
    branches = [],
    branchesLoading = false,
    onChange,
    errors = {},
    showAllErrors = false,
}) => {
    const { t } = useTranslation();
    if (employees.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('employee.upload.noEmployeesToPreview')}
            </Typography>
        );
    }

    const isBranchValid = (branchValue: string) =>
        branches.length === 0 || branches.some((b) => b.id === branchValue);

    return (
        <Stack spacing={2}>
            {employees.map((emp) => {
                const branchValid = isBranchValid(emp.branch);
                const branchDisplayName = branches.find((b) => b.id === emp.branch)?.name ?? emp.branch;
                return (
                    <Card key={emp._rowId} variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                                {emp.firstName} {emp.lastName}
                            </Typography>
                            <Divider sx={{ mb: 1.5 }} />
                            <Stack spacing={1.5}>
                                {/* Branch select */}
                                <FormControl fullWidth size="small" error={!branchValid && (branches.length > 0 || (showAllErrors && !emp.branch))}>
                                    <Select
                                        value={branchValid ? emp.branch : ''}
                                        displayEmpty
                                        onChange={(e) => onChange(emp._rowId, 'branch', e.target.value)}
                                        startAdornment={
                                            branchesLoading ? (
                                                <InputAdornment position="start">
                                                    <CircularProgress size={14} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                        renderValue={(val) =>
                                            val ? (branches.find((b) => b.id === val)?.name ?? String(val)) : <em style={{ color: '#aaa' }}>{t('employee.upload.selectBranchPlaceholder')}</em>
                                        }
                                    >
                                        {branches.map((b) => (
                                            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {!branchValid && (branches.length > 0 || (showAllErrors && !emp.branch)) && (
                                        <FormHelperText>
                                            {errors.branch ?? t('employee.upload.branchNotFound', {name: branchDisplayName})}
                                        </FormHelperText>
                                    )}
                                </FormControl>

                                {/* Text fields */}
                                {TEXT_FIELDS.map(([labelKey, field]) => {
                                    const fieldError = showAllErrors ? errors[field as keyof UploadedEmployeeErrors] : undefined;
                                    return (
                                        <TextField
                                            key={field}
                                            label={t(labelKey)}
                                            size="small"
                                            fullWidth
                                            value={(emp[field] as string) ?? ''}
                                            onChange={(e) => onChange(emp._rowId, field, e.target.value)}
                                            error={!!fieldError}
                                            helperText={fieldError}
                                        />
                                    );
                                })}

                                {/* Employment Type */}
                                <FormControl fullWidth size="small" error={showAllErrors && !!errors.employmentType}>
                                    <Select
                                        value={EMPLOYMENT_TYPE_OPTIONS.includes(emp.employmentType as any) ? emp.employmentType : ''}
                                        displayEmpty
                                        onChange={(e) => onChange(emp._rowId, 'employmentType', e.target.value)}
                                        renderValue={(val) =>
                                            val ? String(val) : <em style={{ color: '#aaa' }}>{t('employee.form.field.employmentType')}</em>
                                        }
                                    >
                                        {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </Select>
                                    {showAllErrors && errors.employmentType && (
                                        <FormHelperText>{errors.employmentType}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Residency/Pension Status */}
                                <FormControl component="fieldset" fullWidth error={showAllErrors && !!errors.residencyStatus}>
                                    <FormLabel component="legend" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                        {t('employee.form.section.residencyStatus')}
                                    </FormLabel>
                                    {RESIDENCY_STATUS_OPTIONS.map((opt) => (
                                        <FormControlLabel
                                            key={opt}
                                            control={
                                                <Switch
                                                    checked={emp.residencyStatus === opt}
                                                    onChange={(e) =>
                                                        onChange(emp._rowId, 'residencyStatus', e.target.checked ? opt : '')
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label={opt}
                                        />
                                    ))}
                                    {showAllErrors && errors.residencyStatus && (
                                        <FormHelperText>{errors.residencyStatus}</FormHelperText>
                                    )}
                                </FormControl>

                                {/* Boolean switches */}
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={emp.status}
                                                onChange={(e) => onChange(emp._rowId, 'status', e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={t('employee.form.activateEmployee')}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={emp.isCitizen}
                                                onChange={(e) => onChange(emp._rowId, 'isCitizen', e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={t('employee.upload.field.citizen')}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={emp.isTaxEmpt}
                                                onChange={(e) => onChange(emp._rowId, 'isTaxEmpt', e.target.checked)}
                                                size="small"
                                            />
                                        }
                                        label={t('employee.form.taxExempt')}
                                    />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default UploadPreviewMobile;
